import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import {
  EMPTY_SCANNER_SESSION,
  SCANNER_SESSION_STORAGE_KEY,
  ScannerContextResponse,
  ScannerSessionState,
} from './barcode-scanner.models';

@Injectable({
  providedIn: 'root',
})
export class BarcodeScannerSessionService {
  private readonly stateSubject = new BehaviorSubject<ScannerSessionState>(this.loadInitialState());
  readonly state$ = this.stateSubject.asObservable();

  get snapshot(): ScannerSessionState {
    return this.stateSubject.value;
  }

  applyContext(context: ScannerContextResponse, servicedStores: string[]): void {
    this.patch({
      domainUuid: context.domain_uuid || this.snapshot.domainUuid,
      poolPointUuid: context.pool_point_uuid || this.snapshot.poolPointUuid,
      servicedStores,
    });
  }

  patch(partial: Partial<ScannerSessionState>): void {
    const nextState: ScannerSessionState = {
      ...this.snapshot,
      ...partial,
      servicedStores: partial.servicedStores ? [...partial.servicedStores] : [...this.snapshot.servicedStores],
    };

    this.stateSubject.next(nextState);
    this.persist(nextState);
  }

  resetWorkflow(): void {
    this.patch({
      poolPointClientUuid: '',
      poolPointClientName: '',
      asnNumber: '',
      shipmentListUuid: '',
      tripNumber: '',
      tripListUuid: '',
      trailerNumber: '',
      storeNumber: '',
      poolPointStoreUuid: '',
    });
  }

  clearAll(): void {
    const nextState = { ...EMPTY_SCANNER_SESSION };
    this.stateSubject.next(nextState);
    sessionStorage.removeItem(SCANNER_SESSION_STORAGE_KEY);
  }

  private loadInitialState(): ScannerSessionState {
    const rawValue = sessionStorage.getItem(SCANNER_SESSION_STORAGE_KEY);

    if (!rawValue) {
      return { ...EMPTY_SCANNER_SESSION };
    }

    try {
      const parsed = JSON.parse(rawValue) as Partial<ScannerSessionState>;
      return {
        ...EMPTY_SCANNER_SESSION,
        ...parsed,
        servicedStores: Array.isArray(parsed.servicedStores) ? parsed.servicedStores.filter((store) => typeof store === 'string') : [],
      };
    } catch {
      sessionStorage.removeItem(SCANNER_SESSION_STORAGE_KEY);
      return { ...EMPTY_SCANNER_SESSION };
    }
  }

  private persist(state: ScannerSessionState): void {
    sessionStorage.setItem(SCANNER_SESSION_STORAGE_KEY, JSON.stringify(state));
  }
}
